// The shared render-group uniforms that modules fetched later write, made here at load (main.js imports this module
// before anything is built). three orders a shared group's uniforms (`renderGroup`) by node id in every program that
// reads them, and a node's id is its place in the order of making: made when a lazy module arrives, a uniform would sit
// before or after the scene's own by network timing, and the programs' text would change from visit to visit (Metal's
// cache serves a returning iPhone only the same text: performance.md "The same text on every visit"). A module that
// needs one more shared uniform makes it here. [check: tests/unit/render-uniforms.test.mjs]
import * as THREE from 'three/webgpu';
import { uniform, uniformArray, renderGroup } from 'three/tsl';

const live = (v) => uniform(v).setGroup(renderGroup);

/** gpu/lightcast.js CAST's values (lazy/lightcast.js writes them every render: why shared, see there) */
export const CAST_LIVE = {
  box: live(new THREE.Vector4(0, 0, 1, 1)), // lcBox: minX, minZ, sizeX, sizeZ (m)
  params: live(new THREE.Vector4(0.5, 0.01, 0, 1 / 3)), // lcP: texel (m), 1 / heightFade^2, strength (look x level), 1 / softMax
  foliage: live(1), // lcFol: the trees' response (foliageGain)
  soft: live(new THREE.Vector4(0, 0, 1, 3)), // lsP: contact, bounce, contact fade height, bounce fade height (m)
  sun: live(new THREE.Color()), // lsSun: the sun's light (colour x intensity)
};

/** lazy/daylight.js DL's values (written once a render; in a material's own group three uploads them only when it sees
 *  that object change, and the rescue set's ground kept dusk's sky light all night: tests/unit/runtime/live-uniforms.test.mjs) */
export const DAYLIGHT_LIVE = {
  sh: uniformArray(Array.from({ length: 9 }, () => new THREE.Vector3())).setGroup(renderGroup), // the sky light's radiance SH (three order), x the env intensity
  snowFill: live(new THREE.Vector3(1, 1, 1)), // 1 + the old snow shade tint / the fill (snow materials only)
};

// lazy/groundlook.js's sun for glints and glow: the first directional light of the scene (main.js adds it first; the
// moon and the shadow-only far cascade come after), read once per render by the uniform itself. uGxEdit (WS-63 editor
// look): the slider positions of world.sparkle, .coldGlow and .tracks (0.5 = the package's look).
let sunLight = null;
const findSun = (scene) => { if (!sunLight && scene?.isScene) sunLight = scene.children.find((o) => o.isDirectionalLight) || null; return sunLight; };
const sunU = (v, set) => live(v).onRenderUpdate(({ scene }, self) => { const s = findSun(scene); if (s) set(self.value, s); });
export const GROUND_SUN = {
  uGxSun: sunU(new THREE.Vector3(0, 1, 0), (v, s) => v.subVectors(s.position, s.target.position).normalize()),
  uGxSunCol: sunU(new THREE.Color(0, 0, 0), (v, s) => v.copy(s.color).multiplyScalar(s.intensity)),
  uGxEdit: live(new THREE.Vector4(0.5, 0.5, 0.5, 0.5)),
};
